/**
 * v3 main screen — floating auth dock (backdrop, dismiss target, scaled form panel).
 */
import React, { useMemo } from 'react';
import { Pressable, StyleSheet, View, type ViewStyle } from 'react-native';
import Animated, { type SharedValue } from 'react-native-reanimated';
import { createBottomSheetChromeStyles } from '../shared/theme/authSheetStyles';
import { consult_row_bottom_corner_radius } from '../shared/theme/styles/welcomeHero';
import type { ThemeColors } from '../shared/theme';
import { MainScreenAuthForms } from '../auth/variants/hero_dock';
import {
  androidMainScreenDismissPressableStyle,
  androidMainScreenDockFloatStyle,
  androidMainScreenDockPanelStaticStyle,
  isMainScreenAndroid,
  mainScreenDockColumnPaddingBottom,
} from '../shared/auth/onboardingMainScreenAndroidParity';
import { MAIN_SCREEN_DOCK, MAIN_SCREEN_FORM } from '../shared/auth/onboardingMainScreenConfig';
import { useMainScreenDockLayerMotion } from '../shared/theme/studio/onboardingMotion';
import { Clickable } from '../components/common/Clickable';
import { useAuthSheetKeyboardInset } from '../shared/auth';

const IS_ANDROID = isMainScreenAndroid();

type MainScreenDockLayerProps = {
  colors: ThemeColors;
  wh: number;
  ww: number;
  insets_bottom: number;
  main_dock: 0 | 1 | 2;
  auth_float_active: boolean;
  dock_backdrop_visible: boolean;
  render_backdrop?: boolean;
  form_mounted: boolean;
  form_scroll_max: number;
  dismiss_label: string;
  dismiss_reserve_bottom: number;
  dockOpen: SharedValue<number>;
  dockExit: SharedValue<number>;
  authFormSwap: SharedValue<number>;
  onDismiss: () => void;
  onSwitchToRegister: () => void;
  onSwitchToLogin: () => void;
};

export function MainScreenDockLayer({
  colors,
  wh,
  ww,
  insets_bottom,
  main_dock,
  auth_float_active,
  dock_backdrop_visible,
  render_backdrop = true,
  form_mounted,
  form_scroll_max,
  dismiss_label,
  dismiss_reserve_bottom,
  dockOpen,
  dockExit,
  authFormSwap,
  onDismiss,
  onSwitchToRegister,
  onSwitchToLogin,
}: MainScreenDockLayerProps) {
  const keyboard_h = useAuthSheetKeyboardInset();
  const dock_panel_theme = useMemo(
    () => ({
      surface: colors.surface,
      primaryUltra: colors.primaryUltra,
      surfaceFade: colors.surfaceFade,
    }),
    [colors.primaryUltra, colors.surface, colors.surfaceFade],
  );
  const { dock_backdrop_a, dock_panel_a } = useMainScreenDockLayerMotion(wh, ww, dock_panel_theme, dockOpen, dockExit);

  const auth_chrome = useMemo(() => createBottomSheetChromeStyles(colors), [colors]);
  const panel_style = useMemo((): ViewStyle => {
    const flat = StyleSheet.flatten(auth_chrome.panel) as ViewStyle;
    const { maxHeight: _m, backgroundColor: _bg, ...rest } = flat;
    return {
      ...rest,
      borderBottomLeftRadius: consult_row_bottom_corner_radius,
      borderBottomRightRadius: consult_row_bottom_corner_radius,
    };
  }, [auth_chrome.panel]);

  const column_style = useMemo(
    (): ViewStyle => ({
      ...StyleSheet.absoluteFillObject,
      alignItems: 'center',
      justifyContent: 'flex-end',
      paddingBottom: mainScreenDockColumnPaddingBottom(wh, insets_bottom, main_dock, keyboard_h),
    }),
    [insets_bottom, keyboard_h, main_dock, wh],
  );

  const panel_w = Math.min(ww * MAIN_SCREEN_DOCK.panelWidthFraction, MAIN_SCREEN_DOCK.panelMaxWidth);
  const panel_max_h = wh * MAIN_SCREEN_DOCK.maxHeightFraction;

  return (
    <View
      style={[
        StyleSheet.absoluteFill,
        { zIndex: MAIN_SCREEN_DOCK.floatZ },
        androidMainScreenDockFloatStyle(auth_float_active),
      ]}
      pointerEvents={auth_float_active ? 'box-none' : 'none'}
      collapsable={false}>
      {render_backdrop && dock_backdrop_visible ? (
        <Animated.View
          pointerEvents="none"
          style={[StyleSheet.absoluteFill, { backgroundColor: colors.backdrop, elevation: 0 }, dock_backdrop_a]}
        />
      ) : null}
      {/*
        Only mount dismiss while the float is active. On web, CSS `pointer-events` is not
        inherited — an absolute-fill dismiss child would steal taps from Sign In / Sign Up
        even when the parent uses pointerEvents="none".
      */}
      {auth_float_active ? (
        IS_ANDROID ? (
          <Pressable
            style={androidMainScreenDismissPressableStyle(dismiss_reserve_bottom)}
            onPress={onDismiss}
            accessibilityRole="button"
            accessibilityLabel={dismiss_label}
          />
        ) : (
          <Clickable style={StyleSheet.absoluteFill} onPress={onDismiss} accessibilityLabel={dismiss_label} pressAnimated={false} haptics={false}>
            <View style={StyleSheet.absoluteFill} />
          </Clickable>
        )
      ) : null}
      <View style={column_style} pointerEvents="box-none">
        <Animated.View
          style={[
            panel_style,
            {
              width: panel_w,
              maxHeight: panel_max_h,
              minHeight: 0,
              overflow: 'hidden',
              transformOrigin: '50% 100% 0px' as const,
              ...(IS_ANDROID ? androidMainScreenDockPanelStaticStyle() : { elevation: 0 }),
            },
            dock_panel_a,
          ]}>
          {form_mounted ? (
            <MainScreenAuthForms
              swap={authFormSwap}
              onDismiss={onDismiss}
              onSwitchToRegister={onSwitchToRegister}
              onSwitchToLogin={onSwitchToLogin}
              mainScreenScrollMaxHeight={form_scroll_max}
              formMotionVariant={MAIN_SCREEN_FORM.motionVariant}
            />
          ) : null}
        </Animated.View>
      </View>
    </View>
  );
}
